import { forwardRef, inject, signal } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NgControl,
  NgModel,
} from '@angular/forms';
import { UserOption } from '@utils/people-list';
import {
  Hang,
  Interest,
  Pueblo,
  UserResourceName,
  Weekday,
} from 'pueblo-types';

export type IHangType = 'freetime' | 'hang';

export interface ITimespan {
  start: Date;
  end: Date;
}

export interface ITimeWindow {
  weekdays: FormControl<Weekday[]>;
  timespan: FormControl<ITimespan>;
}

export enum NotifyPuebloOption {
  EVERYONE = 'everyone',
  INTERESTS = 'interests',
}

export interface PuebloOption {
  pueblo: Pueblo;
  selected: boolean;
  notify: NotifyPuebloOption;
}

export interface InterestOption {
  interest: Interest;
  selected: boolean;
  userCount: number;
}

export declare interface IHangForm {
  title: FormControl<string>;
  emoji: FormControl<string>;
  time: FormControl<ITimespan>;
  location: FormGroup<ILocationForm>;
  details: FormControl<string>;
  link: FormControl<string>;
  visibility: FormControl<Hang.Visibility>;
  pueblos: FormGroup<IPuebloSelectForm>;
  interests: FormGroup<IInterestSelectForm>;
}

export declare interface IUserSelectForm {
  selectedUsers: FormControl<ReadonlyArray<UserOption>>;
}

export declare interface IInterestSelectForm {
  selectedInterests: FormControl<ReadonlyArray<InterestOption>>;
}

export declare interface IPuebloSelectForm {
  selectedPueblos: FormControl<ReadonlyArray<PuebloOption>>;
}

export declare interface IPuebloForm {
  name: FormControl<string>;
  imageUrl: FormControl<string>;
  location: FormGroup<ILocationForm>;
  members: FormGroup<IUserSelectForm>;
  visibility: FormControl<Pueblo.Config.Visibility>;
  postingPermissions: FormControl<Pueblo.Config.PostingPermissions>;
  joinPolicy: FormControl<Pueblo.Config.JoinPolicy>;
}

export declare interface IInterestForm {
  name: FormControl<string>;
  emoji: FormControl<string>;
}

export declare interface IPuebloNotificationPreferencesForm {
  enabledCreators: FormControl<ReadonlyArray<UserResourceName>>;
  defaultEnableCreators: FormControl<boolean>;
  enabledTimeWindows: FormArray<FormGroup<ITimeWindow>>;
  enabledTitles: FormControl<ReadonlyArray<string>>;
  defaultEnableTitles: FormControl<boolean>;
  enabledLocations: FormControl<ReadonlyArray<string>>;
  defaultEnableLocations: FormControl<boolean>;
}

export function assertIsHangForm(
  form: AbstractControl<any, any> | null,
): asserts form is FormGroup<IHangForm> {
  if (!form) throw new TypeError('Form must be defined');

  const hangForm = form as FormGroup<IHangForm>;

  if (
    !hangForm.controls.time ||
    !hangForm.controls.title ||
    !hangForm.controls.location ||
    !hangForm.controls.details ||
    !hangForm.controls.link ||
    !hangForm.controls.emoji
  ) {
    throw new TypeError('Form must be a HangForm');
  }
}

export declare interface ITimeForm {
  date: FormControl<Date>;
  start: FormControl<number>;
  end: FormControl<number>;
}

export function assertIsTimeForm(
  form: AbstractControl<any, any> | null,
): asserts form is FormGroup<ITimeForm> {
  if (!form) throw new TypeError('Form must be defined');

  const timeForm = form as FormGroup<ITimeForm>;

  if (
    !timeForm.controls.start ||
    !timeForm.controls.end ||
    !timeForm.controls.date
  ) {
    throw new TypeError('Form must be a TimeForm');
  }
}

export declare interface ILocationForm {
  locationAddress: FormControl<string>;
  locationName: FormControl<string>;
  latitude: FormControl<number>;
  longitude: FormControl<number>;
}

export function assertIsLocationForm(
  form: AbstractControl<any, any> | null,
): asserts form is FormGroup<ILocationForm> {
  if (!form) throw new TypeError('Form must be defined');

  const locationForm = form as FormGroup<ILocationForm>;

  if (
    !locationForm.controls.locationName ||
    !locationForm.controls.locationAddress
  ) {
    throw new TypeError('Form must be a LocationForm');
  }
}

export function assertIsUserSelectForm(
  form: AbstractControl<any, any> | null,
): asserts form is FormGroup<IUserSelectForm> {
  if (!form) throw new TypeError('Form must be defined');

  const userSelectForm = form as FormGroup<IUserSelectForm>;

  if (!userSelectForm.controls.selectedUsers) {
    throw new TypeError('Form must be a UserSelectForm');
  }
}

export function assertIsInterestSelectForm(
  form: AbstractControl<any, any> | null,
): asserts form is FormGroup<IInterestSelectForm> {
  if (!form) throw new TypeError('Form must be defined');

  const interestSelectForm = form as FormGroup<IInterestSelectForm>;

  if (!interestSelectForm.controls.selectedInterests) {
    throw new TypeError('Form must be a InterestSelectForm');
  }
}

export function assertIsPuebloSelectForm(
  form: AbstractControl<any, any> | null,
): asserts form is FormGroup<IPuebloSelectForm> {
  if (!form) throw new TypeError('Form must be defined');

  const puebloSelectForm = form as FormGroup<IPuebloSelectForm>;

  if (!puebloSelectForm.controls.selectedPueblos) {
    throw new TypeError('Form must be a PuebloSelectForm');
  }
}

export function providePbForm(formType: any) {
  return {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => formType),
    multi: true,
  };
}

export abstract class PbFormBase<
  T extends { [K in keyof T]: AbstractControl<any, any> },
> implements ControlValueAccessor
{
  protected formGroup = this.emptyFormGroup();
  protected disabled = signal<boolean>(false);
  protected onChange: any = (_: T) => {};
  protected onTouched: any = () => {};

  protected abstract emptyFormGroup(): FormGroup<T>;

  writeValue(value: any): void {
    this.formGroup.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: T) => {}): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}

export abstract class PbFormValueBase<T> implements ControlValueAccessor {
  protected value = signal(this.defaultValue());
  protected disabled = signal(false);

  protected onChange = (_: T) => {};
  protected onTouched = () => {};

  protected abstract defaultValue(): T;

  writeValue(value: T): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: T) => {}): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}

// Injects the form control into a component that implements ControlValueAccessor.
// https://medium.com/netanelbasal/forwarding-form-controls-to-custom-control-components-in-angular-701e8406cc55
class NoopValueAccessor implements ControlValueAccessor {
  writeValue() {}
  registerOnChange() {}
  registerOnTouched() {}
}
export function injectNgControl() {
  const ngControl = inject(NgControl, { self: true, optional: true });

  if (!ngControl) throw new Error('...');

  if (
    ngControl instanceof FormControlDirective ||
    ngControl instanceof FormControlName ||
    ngControl instanceof NgModel
  ) {
    ngControl.valueAccessor = new NoopValueAccessor();
    return ngControl;
  }

  throw new Error(`...`);
}
